import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Monitor, Shield, Users, Settings, Laptop, Smartphone } from 'lucide-react'
import SEO from '../../components/SEO'

const ManagedDesktops = () => {
  const features = [
    {
      icon: Monitor,
      title: 'Complete Device Management',
      description: 'Comprehensive management of all your computers, tablets, and devices across all locations.'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Military-grade security protecting your devices and data with advanced threat protection.'
    },
    {
      icon: Smartphone,
      title: 'Mobile Integration',
      description: 'Seamless management of iPhones, iPads, and mobile devices with unified security policies.'
    },
    {
      icon: Users,
      title: 'Family Coverage',
      description: 'Extend elite protection and support to family members\' devices and accounts.'
    },
    {
      icon: Settings,
      title: 'Automated Maintenance',
      description: 'Proactive updates, optimization, and maintenance performed automatically in the background.'
    },
    {
      icon: Laptop,
      title: 'Anywhere Access',
      description: 'Secure remote access to your systems from anywhere in the world.'
    }
  ]

  return (
    <>
      <SEO 
        title="Managed Desktops - Praetorian Works"
        description="Elite managed desktop and device services for HNW and UHNW clients. Enterprise-grade management, security, and support for all your computing devices."
        keywords="managed desktop HNW, elite device management, private computer support, luxury IT services"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Monitor className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Managed
                <span className="text-primary-500 ml-4">Desktops</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Effortless technology with enterprise-class management for all your devices. Your computers, 
                tablets, and phones optimized, secured, and maintained to perform flawlessly—always.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-secondary-800 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Technology That <span className="text-primary-500">Just Works</span>
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Your time is valuable. You shouldn't waste it dealing with slow computers, software updates, 
                or security concerns. Our Managed Desktop service ensures every device you use is optimized, 
                protected, and performing at its peak—whether you're at home, traveling, or anywhere in the world.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                We manage everything behind the scenes: updates, backups, security, performance optimization, 
                and proactive monitoring. You simply enjoy fast, reliable technology that never lets you down.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Experience Effortless Technology
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Discover how Managed Desktops can simplify your digital life and maximize your productivity.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Learn More
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ManagedDesktops
